import React, { useState } from 'react';
import { Button } from 'reactstrap';


import { Card } from '../base/components/CardAccordion';

import DataStore from '../base/plumbing/DataStore';
import PropControl from '../base/components/PropControl';

import ImageObject from '../base/data/ImageObject';
import Icon from '../base/components/Icon';

import BlindUpload from '../base/components/BlindUpload';
import Misc from './MiscOverrides';
import { A } from '../base/plumbing/glrouter';
import SubCard from '../base/components/SubCard';


/**
 * Moveable item in a list for imageList in NGOs
 * @param {Object} p
 * @param {ImageObject} p.imgObj
 * @param {String[]} p.path
 * @param {Number} p.index
 * @param {Number} p.listLength
 * 
 */
function ImageListItemControl({ imgObj, listPath, index, listLength, moveItem}) {
	const imgUrl = imgObj.contentUrl || imgObj.url;
	const itemPath = listPath.concat(index);

	const canMoveUp = index > 0;
	const canMoveDown = index < listLength - 1;
	const canMove = canMoveUp || canMoveDown;

	return <SubCard className="flex-row ngo-image-item p-2">
		<div className="info flex-row align-items-start">
			<A href={imgUrl}>
				<Misc.ImgThumbnail url={imgUrl || '/img/no-image.svg'}
					className="mr-2" style={{width: '5rem', height: '5rem'}}
				/>
			</A>
			<div>
				{imgObj.width ? <div>
					{imgObj.width}<Icon name="x" />{imgObj.height}
				</div> : <div>
					Size unknown. <Button size="sm" onClick={() => probeImageDimensions(imgUrl, listPath)}>Fix</Button>
				</div>}
				{imgObj.contentSize && <div>{imgObj.contentSize}</div>}
				</div>
		</div>
		<div className="controls flex-row align-items-end px-2">
		<PropControl prop="caption" type="textarea" path={itemPath} label="Caption or description" className="mx-2 my-0" />
			<PropControl prop="url" type="url" path={itemPath} label="Original location" className="mx-2 my-0" />
		</div>
		<div className="arrange flex-column justify-content-end">
			{canMove && <div className="mb-1">
				<Button color="secondary" title="Move up" className="mr-1" disabled={!canMoveUp} onClick={() => moveItem(index, index-1)}>↑</Button>
				<Button color="secondary" title="Move down" disabled={!canMoveDown} onClick={() => moveItem(index, index+1)}>↓</Button>
			</div>}
			<Button color="danger" className="w-100" onClick={() => moveItem(index)}>Delete</Button>
		</div>
	</SubCard>;
}


/**
 * Use the Image constructor to load an image URL, probe its dimensions, and update the corresponding ImageObject
 * @param {String} url The location of the image to probe
 * @param {String[]} listPath Location of the list to update
 */
const probeImageDimensions = (url, listPath) => {
	const imgEl = new Image();
	imgEl.addEventListener('load', () => {
		// This seems roundabout, but it's possible the item's position in the
		// containing list will have changed while the image was loading.
		const imageList = DataStore.getValue(listPath);
		const objIndex = imageList.findIndex(o => o.contentUrl === url);
		if (objIndex < 0) return; // Not in the list, nothing to update

		const oldObj = imageList[objIndex];
		const { naturalWidth: width, naturalHeight: height } = imgEl;
		const newObj = new ImageObject({...oldObj, width, height});
		imageList[objIndex] = newObj;
		DataStore.setValue(listPath, imageList);
	});
	imgEl.src = url;
};


/**
 * Editor for a list of ImageObjects, as found on NGOs.
 * TODO (a) Refactor to use PropControlList
 * TODO (b) Copy item-rearranging functionality to PropControlList
 * @param {Object} p
 * @param {String[]} p.path DataStore path to the object containing the list
 * @param {String} p.prop The list's field name
 * @param {String} p.title Title for the expandable card
 * @param {String} p.desc Notes to show above the list
 * @returns {React.Element}
 */
function NGOImageListControls({ path, prop, title, desc }) {
	const [open, setOpen] = useState(false);
	const toggle = () => setOpen(!open);

	const listPath = path.concat(prop);
	const imageList = DataStore.getValue(listPath) || [];

	/** Add an uploaded image to the list */
	const onUpload = (response) => {
		const { contentSize, uploadDate, url: contentUrl } = response.cargo;
		// Update with basic info from UploadServlet
		const imgObj = new ImageObject({ contentUrl, contentSize, uploadDate });
		imageList.push(imgObj);
		// Attempt to load the image and find its dimensions
		probeImageDimensions(contentUrl, listPath);
		DataStore.setValue(listPath, imageList);
	};

	/** Move an array entry, or delete it (ie move to nowhere) if "to" omitted */
	const moveItem = (from, to) => {
		const max = imageList.length - 1;
		const fromOOB = (from <= 0 || from > max);
		const isMove = (to != null);
		const toOOB = (isMove && (to <= 0 || to > max));
		if (fromOOB || toOOB) return; // Shouldn't happen, but.
		const [item] = imageList.splice(from, 1);
		if (to) imageList.splice(to, 0, item);
		DataStore.setValue(listPath.concat(prop), imageList);
	};

	const itemCommon = { listPath, moveItem, listLength: imageList.length };

	return (
		<Card title={title} collapse={!open} onHeaderClick={toggle} className="ngo-image-list-editor">
			{desc && <div>{desc}</div>}
			{imageList.map((imgObj, i) => (
				<ImageListItemControl {...itemCommon} key={i} imgObj={imgObj} index={i} />
			))}
			<BlindUpload label="Drop an image here to add" onUpload={onUpload} />
		</Card>
	);
}

export default NGOImageListControls;
